import { useEffect, useState } from 'react';
import styles from './mobile-case-tracking-cards.module.scss';
import { StageStatus } from '../stage-status/StageAndStatus';
import { MobileCaseTrackingTabs } from '../../atoms/mobile-case-tracking-tabs/MobileCaseTrackingTabs';
import { Typography } from '@sanlam-glacier/app-common/components';
import { formatDateAsString } from 'utils/string-handling/human-readable-text';
import Pagination from '../../atoms/mobile-case-tracking-pagination/MobileCaseTrackingPagination';
import { CaseType } from 'modules/case-tracking/types/case-tracking.types';
import { useNavigate } from 'react-router-dom';

interface Props {
  cases: CaseType[];
  isTablet: boolean;
}

export const MobileCaseTrackingCards = ({ cases, isTablet }: Props) => {
  const [statusFilter, setStatusFilter] = useState<'all' | 'inProgress' | 'completed'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const navigate = useNavigate();

  const sortedData = [...cases].sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
  );

  const filteredByStatus = sortedData.filter((row) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'inProgress') return !row.isCancelled && row.stage < 4;
    if (statusFilter === 'completed') return row.stage === 4;
    return true;
  });

  const totalPages = Math.ceil(filteredByStatus.length / itemsPerPage);
  const paginatedData = filteredByStatus.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setStatusFilter(statusFilter);
    setCurrentPage(1);
  }, [statusFilter]);

  const handleRowClick = (row: CaseType) => {
    navigate(`instruction/${row.instructionNumber}`);
  };

  return (
    <div className={styles.caseTracking}>
      <div className={styles.tabs}>
        <MobileCaseTrackingTabs statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
      </div>

      <div className={styles.cards}>
        {paginatedData.length > 0 ? (
          paginatedData.map((row) => {
            const isCancelled = row.isCancelled;

            return (
              <button key={row.id} className={styles.card} onClick={() => handleRowClick(row)}>
                <div className={styles.statusContainer}>
                  <div className={styles.statusTextContainer}>
                    <Typography
                      variant={Typography.VARIANT.B3}
                      weight={Typography.WEIGHT.MEDIUM}
                      className={styles.instructionNumberText}
                    >
                      Instruction #
                      <span className={styles.instructionNumber}>
                        {row.instructionNumber ?? '-'}
                      </span>
                    </Typography>

                    <Typography
                      variant={Typography.VARIANT.B4}
                      weight={Typography.WEIGHT.MEDIUM}
                      className={styles.statusText}
                    >
                      {row.stageDescription ?? '-'}
                    </Typography>
                  </div>

                  <StageStatus
                    stage={row.stage}
                    stageDescription={row.stageDescription}
                    isCancelled={isCancelled}
                    isTablet={isTablet}
                  />
                </div>

                <div className={styles.cardTextContainer}>
                  <Typography
                    color={Typography.COLOR.TERTIARY}
                    variant={Typography.VARIANT.B4}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.label}
                  >
                    Created
                  </Typography>
                  <Typography
                    color={Typography.COLOR.DEFAULT}
                    variant={Typography.VARIANT.B4}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.value}
                  >
                    {formatDateAsString(new Date(row.created)) ?? '-'}
                  </Typography>

                  <Typography
                    color={Typography.COLOR.TERTIARY}
                    variant={Typography.VARIANT.B4}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.label}
                  >
                    Client name
                  </Typography>
                  <Typography
                    color={Typography.COLOR.DEFAULT}
                    variant={Typography.VARIANT.B4}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.value}
                  >
                    {row.clientName ?? '-'}
                  </Typography>

                  <Typography
                    color={Typography.COLOR.TERTIARY}
                    variant={Typography.VARIANT.B4}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.label}
                  >
                    Instruction type
                  </Typography>
                  <Typography
                    color={Typography.COLOR.DEFAULT}
                    variant={Typography.VARIANT.B4}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.value}
                  >
                    {row.instructionType ?? '-'}
                  </Typography>

                  <Typography
                    color={Typography.COLOR.TERTIARY}
                    variant={Typography.VARIANT.B4}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.label}
                  >
                    Sub type
                  </Typography>
                  <Typography
                    color={Typography.COLOR.DEFAULT}
                    variant={Typography.VARIANT.B4}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.value}
                  >
                    {row.instructionCategory ?? '-'}
                  </Typography>

                  <Typography
                    color={Typography.COLOR.TERTIARY}
                    variant={Typography.VARIANT.B4}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.label}
                  >
                    Last modified
                  </Typography>
                  <Typography
                    color={Typography.COLOR.DEFAULT}
                    variant={Typography.VARIANT.B4}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.value}
                  >
                    {formatDateAsString(new Date(row.lastModified)) ?? '-'}
                  </Typography>
                </div>
              </button>
            );
          })
        ) : (
          <p className={styles.noResults}>No cases found</p>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};
